import { useEffect, useMemo, useState, type ReactNode } from 'react';
import StatusBadge, { type StatusTone } from './StatusBadge';
import { cn } from '../../lib/utils';

export interface HierarchyTreeNode {
  id: string;
  label: string;
  subtitle?: string;
  statusLabel?: string;
  statusTone?: StatusTone;
  quantityLabel?: string;
  qrCountLabel?: string;
  actions?: ReactNode;
  children?: HierarchyTreeNode[];
}

export interface HierarchyTreeProps {
  nodes: HierarchyTreeNode[];
  className?: string;
}

const collectExpandedState = (nodes: HierarchyTreeNode[]) => {
  const expanded: Record<string, boolean> = {};
  const walk = (list: HierarchyTreeNode[]) => {
    list.forEach((node) => {
      if (node.children && node.children.length > 0) {
        expanded[node.id] = true;
        walk(node.children);
      }
    });
  };
  walk(nodes);
  return expanded;
};

export default function HierarchyTree({ nodes, className }: HierarchyTreeProps) {
  const defaultExpanded = useMemo(() => collectExpandedState(nodes), [nodes]);
  const [expandedState, setExpandedState] = useState<Record<string, boolean>>(defaultExpanded);

  useEffect(() => {
    setExpandedState(defaultExpanded);
  }, [defaultExpanded]);

  const toggleNode = (nodeId: string) => {
    setExpandedState((prev) => ({ ...prev, [nodeId]: !prev[nodeId] }));
  };

  const renderNode = (node: HierarchyTreeNode, depth: number) => {
    const hasChildren = !!node.children?.length;
    const expanded = hasChildren ? expandedState[node.id] !== false : false;
    const childGroupId = `${node.id}-children`;

    return (
      <div key={node.id}>
        <div
          className="flex flex-wrap items-center gap-3 border-b border-gray-100 px-4 py-3 last:border-b-0"
          style={{ paddingLeft: `${depth * 16 + 16}px` }}
          role="treeitem"
          aria-level={depth + 1}
          aria-expanded={hasChildren ? expanded : undefined}
        >
          <div className="flex min-w-0 flex-1 items-start gap-2">
            {hasChildren ? (
              <button
                type="button"
                onClick={() => toggleNode(node.id)}
                className="mt-0.5 rounded p-0.5 text-gray-500 transition hover:bg-gray-100 hover:text-gray-700"
                aria-label={expanded ? `Collapse ${node.label}` : `Expand ${node.label}`}
                aria-controls={childGroupId}
              >
                {expanded ? (
                  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" />
                  </svg>
                ) : (
                  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" />
                  </svg>
                )}
              </button>
            ) : (
              <span className="mt-1 h-2 w-2 rounded-full bg-emerald-300" aria-hidden="true" />
            )}
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-gray-900">{node.label}</p>
              {node.subtitle && <p className="text-xs text-gray-500">{node.subtitle}</p>}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {node.quantityLabel && (
              <span className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-gray-600">
                {node.quantityLabel}
              </span>
            )}
            {node.qrCountLabel && (
              <span className="rounded-full border border-blue-200 bg-blue-50 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-blue-700">
                {node.qrCountLabel}
              </span>
            )}
            {node.statusLabel && (
              <StatusBadge
                tone={node.statusTone ?? 'muted'}
                size="sm"
                label={node.statusLabel}
              />
            )}
            {node.actions}
          </div>
        </div>

        {hasChildren && expanded && (
          <div role="group" id={childGroupId}>
            {node.children?.map((childNode) => renderNode(childNode, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <section
      className={cn('overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm', className)}
      role="tree"
      aria-label="Packaging hierarchy"
    >
      {nodes.length === 0 ? (
        <p className="px-4 py-6 text-sm text-gray-500">No hierarchy nodes configured.</p>
      ) : (
        nodes.map((node) => renderNode(node, 0))
      )}
    </section>
  );
}
